// The per-type panel on #/edit (T57 WP4).
//
// Everything here is optional and saves independently of the main profile form,
// so a provider can fill one chip and leave. The profile is already published;
// this only makes it better. Nothing in this panel can block anything.

import { supabase } from './supabase';
import {
  groupFor,
  missingFor,
  sosEligible,
  toStringList,
  readCoverageRadius,
  BOOKING_PLATFORMS,
  CUISINES,
  DIETARY,
  MECHANIC_SERVICE_TYPES,
  SERVICES_OFFERED,
  type PerTypeProfile,
} from './per-type';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

function chips(name: string, options: ReadonlyArray<readonly [string, string]>, chosen: string[]): string {
  return options.map(([value, label]) => `
    <button type="button" class="pt-chip${chosen.includes(value) ? ' pt-on' : ''}"
            data-chipgroup="${esc(name)}" data-value="${esc(value)}"
            aria-pressed="${chosen.includes(value)}">${esc(label)}</button>`).join('');
}

function triState(name: string, label: string, value: boolean | null): string {
  const opt = (v: string, text: string, on: boolean): string => `
    <button type="button" class="pt-seg${on ? ' pt-on' : ''}" data-tri="${esc(name)}" data-value="${v}">${text}</button>`;
  return `
    <div class="pt-field">
      <label>${esc(label)}</label>
      <div class="pt-segs">
        ${opt('true', 'Yes', value === true)}
        ${opt('false', 'No', value === false)}
        ${opt('null', 'Not sure', value === null)}
      </div>
    </div>`;
}

export interface PerTypeOptions {
  container: HTMLElement;
  profileId: string;
  profile: PerTypeProfile;
}

export function mountPerTypeSection(opts: PerTypeOptions): void {
  const { container, profileId } = opts;
  const p: PerTypeProfile = { ...opts.profile };
  const group = groupFor(p.business_type);
  if (!group) return;   // not a pilot type — render nothing at all

  container.className = 'field-section pt-section';

  const cuisineOptions = CUISINES.map((c) => [c, c] as const);
  const body = group === 'food'
    ? `
      <div class="pt-field">
        <label>What kind of food?</label>
        <div class="pt-chips" data-group="cuisine_types">
          ${chips('cuisine_types', cuisineOptions, toStringList(p.cuisine_types))}
        </div>
      </div>
      <div class="pt-field">
        <label>Dietary options you can cover</label>
        <div class="pt-chips" data-group="dietary_options">
          ${chips('dietary_options', DIETARY, toStringList(p.dietary_options))}
        </div>
      </div>
      ${triState('reservation_required', 'Do you need a reservation?', p.reservation_required)}
      ${triState('outdoor_seating', 'Outdoor seating?', p.outdoor_seating)}
      <div class="pt-cols">
        <div class="pt-field">
          <label for="pt-capacity">Roughly how many seats?</label>
          <input type="number" id="pt-capacity" min="0" max="10000" inputmode="numeric"
                 value="${p.capacity ?? ''}" />
        </div>
        <div class="pt-field">
          <label>Booking platform</label>
          <p class="pt-readonly">${p.booking_platform
            ? esc(BOOKING_PLATFORMS.find(([v]) => v === p.booking_platform)?.[1] ?? p.booking_platform)
            : 'Not connected yet'}</p>
          <p class="muted small">John connects booking platforms, so what travellers are told
            about booking you has been checked. Send him your link and he'll set it up.</p>
        </div>
      </div>
      <div class="pt-field">
        <label for="pt-booking-url">Booking link</label>
        <input type="url" id="pt-booking-url" placeholder="https://…"
               value="${esc(p.external_booking_url)}" />
      </div>`
    : `
      <div class="pt-field">
        <label for="pt-sos">Available when a rider breaks down?</label>
        <select id="pt-sos">
          ${MECHANIC_SERVICE_TYPES.map(([v, l]) =>
            `<option value="${esc(v)}"${(p.mechanic_service_type ?? '') === v ? ' selected' : ''}>${esc(l)}</option>`).join('')}
        </select>
        <p class="pt-sos-note muted small"></p>
      </div>
      ${triState('mobile_service', 'Do you travel to the rider?', p.mobile_service)}
      ${triState('emergency_callout', 'Do you take emergency callouts?', p.emergency_callout)}
      <div class="pt-cols">
        <div class="pt-field">
          <label for="pt-response">How fast can you usually get there?</label>
          <input type="text" id="pt-response" placeholder="e.g. within 60 minutes"
                 value="${esc(p.response_time)}" />
        </div>
        <div class="pt-field">
          <label for="pt-radius">How far do you travel? (km)</label>
          <input type="number" id="pt-radius" min="0" max="200" inputmode="numeric"
                 value="${readCoverageRadius(p.coverage_zones)?.radius_km ?? ''}" />
        </div>
      </div>
      <div class="pt-field">
        <label>What can you fix?</label>
        <div class="pt-chips" data-group="services_offered">
          ${chips('services_offered', SERVICES_OFFERED.map((s) => [s, s] as const),
                  toStringList(p.services_offered))}
        </div>
      </div>`;

  container.innerHTML = `
    <h2>${group === 'food' ? 'About your place' : 'Your callout service'}</h2>
    <p class="muted small">All optional — your profile is already live. Each of these just helps
      travellers find you for the right reason.</p>
    ${body}
    <div class="pt-actions">
      <button type="button" class="pt-save" id="pt-save">Save these details</button>
      <span id="pt-missing" class="muted small"></span>
    </div>
    <p id="pt-status" class="status" aria-live="polite"></p>
  `;

  const q = <T extends HTMLElement>(sel: string): T | null => container.querySelector<T>(sel);
  const status = q<HTMLElement>('#pt-status')!;
  const setStatus = (msg: string, kind: '' | 'success' | 'error'): void => {
    status.textContent = msg;
    status.className = kind ? `status status-${kind}` : 'status';
  };

  // ── local edit state ──
  const chosen: Record<string, string[]> = {
    cuisine_types: toStringList(p.cuisine_types),
    dietary_options: toStringList(p.dietary_options),
    services_offered: toStringList(p.services_offered),
  };
  const tri: Record<string, boolean | null> = {
    reservation_required: p.reservation_required,
    outdoor_seating: p.outdoor_seating,
    mobile_service: p.mobile_service,
    emergency_callout: p.emergency_callout,
  };

  container.querySelectorAll<HTMLButtonElement>('button[data-chipgroup]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const g = chip.dataset.chipgroup!;
      const v = chip.dataset.value!;
      const list = chosen[g] ?? [];
      const at = list.indexOf(v);
      if (at >= 0) list.splice(at, 1);
      else list.push(v);
      chosen[g] = list;
      chip.classList.toggle('pt-on', at < 0);
      chip.setAttribute('aria-pressed', String(at < 0));
    });
  });

  container.querySelectorAll<HTMLButtonElement>('button[data-tri]').forEach((seg) => {
    seg.addEventListener('click', () => {
      const g = seg.dataset.tri!;
      const raw = seg.dataset.value!;
      tri[g] = raw === 'null' ? null : raw === 'true';
      container.querySelectorAll<HTMLButtonElement>(`button[data-tri="${g}"]`)
        .forEach((s) => s.classList.toggle('pt-on', s === seg));
    });
  });

  // booking_platform and the four IDs are ALL operator-only (John's ruling,
  // amended 2026-09-05): tour-context.ts renders "[Bookable via <platform>]" from
  // booking_platform alone, with no ID, which is an unvetted commercial claim
  // (ruling 042). The provider sees the current value read-only. Rendering an
  // editable control whose value the trigger silently reverts would be worse than
  // showing nothing — it would report "Saved" and change nothing.

  // The SOS note reads the operator-only half of the rule honestly.
  const sosSel = q<HTMLSelectElement>('#pt-sos');
  const sosNote = q<HTMLElement>('.pt-sos-note');
  const syncSos = (): void => {
    if (!sosSel || !sosNote) return;
    if (!sosSel.value) {
      sosNote.textContent = 'Riders will not be offered your number when they need help.';
    } else if (p.is_active !== true) {
      sosNote.textContent =
        'Your listing is paused, so you will not appear for SOS yet — email john@ebikesflorence.com.';
    } else {
      sosNote.textContent = 'Riders nearby can be pointed to you when they break down.';
    }
  };
  sosSel?.addEventListener('change', syncSos);
  syncSos();

  const renderMissing = (): void => {
    const left = missingFor(p);
    const el = q<HTMLElement>('#pt-missing');
    if (!el) return;
    el.textContent = left.length === 0
      ? 'Nothing missing — thank you.'
      : `Still blank: ${left.map((m) => m.label).join(', ')}`;
  };
  renderMissing();

  // ── save ──
  q<HTMLButtonElement>('#pt-save')?.addEventListener('click', async () => {
    const patch: Record<string, unknown> = {};

    if (group === 'food') {
      patch.cuisine_types = chosen.cuisine_types;
      patch.dietary_options = chosen.dietary_options;
      patch.reservation_required = tri.reservation_required;
      patch.outdoor_seating = tri.outdoor_seating;
      const cap = q<HTMLInputElement>('#pt-capacity')?.value.trim() ?? '';
      patch.capacity = cap === '' ? null : Number(cap);
      const url = q<HTMLInputElement>('#pt-booking-url')?.value.trim() ?? '';
      patch.external_booking_url = url === '' ? null : url;
      // booking_platform and the four ID columns are deliberately NOT in this
      // patch. All five are operator-only, and the trigger would revert them
      // silently — sending them would make the client look like it had saved
      // something it had not.
    } else {
      const sos = sosSel?.value ?? '';
      patch.mechanic_service_type = sos === '' ? null : sos;
      patch.mobile_service = tri.mobile_service;
      patch.emergency_callout = tri.emergency_callout;
      const rt = q<HTMLInputElement>('#pt-response')?.value.trim() ?? '';
      patch.response_time = rt === '' ? null : rt;
      patch.services_offered = chosen.services_offered;
      const km = q<HTMLInputElement>('#pt-radius')?.value.trim() ?? '';
      const existing = readCoverageRadius(p.coverage_zones);
      patch.coverage_zones = km === ''
        ? []
        : [{
            kind: 'radius',
            // Fall back to the Florence centroid until the profile pin is wired
            // through — a radius with no centre is not a fact we can use.
            lat: existing?.lat ?? 43.7696,
            lng: existing?.lng ?? 11.2558,
            radius_km: Number(km),
          }];
    }

    setStatus('Saving…', '');
    const { error } = await supabase.from('provider_profiles').update(patch).eq('id', profileId);
    if (error) {
      setStatus(error.message, 'error');
      return;
    }
    Object.assign(p, patch);
    setStatus('Saved.', 'success');
    syncSos();
    renderMissing();
    if (group === 'mechanic') {
      setStatus(sosEligible(p) ? 'Saved — you are listed for SOS.' : 'Saved.', 'success');
    }
  });
}
